import axios from "axios";

// P3 — runtime-config : l'URL backend est résolue à l'EXÉCUTION (image agnostique de l'environnement).
// Priorité : window.__CROS_CONFIG__.BACKEND_URL (injecté par le conteneur) > REACT_APP_BACKEND_URL (dev)
// > "" (vide = same-origin, /api relatif derrière le reverse proxy Nginx).
const _rt = (typeof window !== "undefined" && window.__CROS_CONFIG__) || {};
const _backend = _rt.BACKEND_URL != null ? _rt.BACKEND_URL : (process.env.REACT_APP_BACKEND_URL || "");
const API = `${String(_backend).replace(/\/+$/, "")}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const CRITICALITY = {
  critical: { label: "Critique", cls: "bg-red-500/15 text-red-400 border-red-500/30", dot: "#FF3B30" },
  high: { label: "Élevée", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30", dot: "#FF9F0A" },
  medium: { label: "Moyenne", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30", dot: "#0A84FF" },
  low: { label: "Faible", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", dot: "#71717A" },
};

export const STATUS = {
  active: { label: "Actif", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  inactive: { label: "Inactif", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  degraded: { label: "Dégradé", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  offline: { label: "Indisponible", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  unavailable: { label: "Indisponible", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  planned: { label: "Planifié", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};

export const ENVIRONMENTS = {
  production: "Production",
  staging: "Préproduction",
  development: "Développement",
  dr: "Reprise (PRA)",
};

export const CATEGORIES = {
  server: "Serveur",
  workstation: "Poste de travail",
  database: "Base de données",
  db: "Base de données",
  network: "Réseau",
  identity: "Identité",
  scada: "SCADA / OT",
  application: "Application",
  storage: "Stockage",
};

// Couche de traduction UI — valeurs techniques (anglais) -> libellés FR.
export const tCrit = (v) => CRITICALITY[v]?.label || v;
export const tStatus = (v) => STATUS[v]?.label || v;
export const tEnv = (v) => ENVIRONMENTS[v] || v;
export const tCategory = (v) => CATEGORIES[v] || v;

export const EDGE_TYPES = {
  supports: { label: "Supporte", color: "#32D74B" },
  depends_on: { label: "Dépend de", color: "#71717A" },
  uses: { label: "Utilise", color: "#5E5CE6" },
  authenticates_via: { label: "S'authentifie via", color: "#0066FF" },
  hosted_on: { label: "Est hébergé sur", color: "#64D2FF" },
  hosts: { label: "Héberge", color: "#32D74B" },
  communicates_with: { label: "Communique avec", color: "#FF9F0A" },
  protected_by: { label: "Protégé par", color: "#30D158" },
  backed_up_by: { label: "Sauvegardé par", color: "#BF5AF2" },
};

export const REL_STATUS = {
  active: { label: "Active", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  inactive: { label: "Inactive", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  planned: { label: "Planifiée", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
};
export const DIRECTIONS = { directed: "Dirigée", bidirectional: "Bidirectionnelle" };
export const REL_TYPES = [
  "supports", "depends_on", "uses", "authenticates_via",
  "hosted_on", "communicates_with", "protected_by", "backed_up_by",
];
export const ALT_STATUS = {
  disponible: "Disponible", degradee: "Dégradée", indisponible: "Indisponible", non_verifiee: "Non vérifiée",
};
export const SWITCH_MODE = {
  automatique: "Automatique", manuelle: "Manuelle", non_renseignee: "Non renseignée",
};
export const VALIDATION = {
  declaree: "Déclarée", testee: "Testée", verifiee_techniquement: "Vérifiée techniquement", non_verifiee: "Non vérifiée",
};
export const CONFIDENCE_CLS = {
  "Élevé": "text-green-400", "Moyen": "text-orange-400", "Faible": "text-red-400",
};

// FAILURE_DOMAIN — identifiants techniques (anglais) -> libellés FR.
export const FD_TYPES = [
  "DATACENTER", "ROOM", "CLOUD_REGION", "AVAILABILITY_ZONE", "CLUSTER",
  "HYPERVISOR", "STORAGE", "NETWORK", "POWER", "PROVIDER",
  "THIRD_PARTY", "IDENTITY_DOMAIN", "OTHER",
];
export const FD_TYPE_LABELS = {
  DATACENTER: "Datacenter", ROOM: "Salle", CLOUD_REGION: "Région cloud",
  AVAILABILITY_ZONE: "Zone de disponibilité", CLUSTER: "Cluster", HYPERVISOR: "Hyperviseur",
  STORAGE: "Stockage", NETWORK: "Réseau", POWER: "Alimentation", PROVIDER: "Fournisseur",
  THIRD_PARTY: "Service tiers", IDENTITY_DOMAIN: "Domaine d'identité", OTHER: "Autre",
};
export const tFdType = (v) => FD_TYPE_LABELS[v] || v;

// FAILOVER_TEST
export const TEST_RESULTS = {
  reussi: { label: "Réussi", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  reussi_reserves: { label: "Réussi avec réserves", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  echoue: { label: "Échoué", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  non_concluant: { label: "Non concluant", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const TEST_TYPES = {
  complet: "Bascule complète", partiel: "Bascule partielle", documentaire: "Revue documentaire",
  inopine: "Test inopiné", autre: "Autre",
};
export const tTestResult = (v) => TEST_RESULTS[v]?.label || v;
export const tTestType = (v) => TEST_TYPES[v] || v;

// Qualification structurelle -> classe de couleur.
export const RESILIENCE_CLS = {
  redondance_verifiee: "text-green-400",
  redondance_partielle: "text-yellow-400",
  resilience_reduite: "text-yellow-400",
  bascule_non_validee: "text-orange-400",
  alt_indisponible: "text-orange-400",
  alt_non_verifiee: "text-orange-400",
  aucune_alternative: "text-orange-400",
};
export const FRESHNESS_CLS = {
  a_jour: "text-green-400", avertissement: "text-yellow-400", expire: "text-red-400", inconnu: "text-muted-foreground",
};

// Catégories de filtre de résilience (source unique = Structural Resilience Engine).
export const RESILIENCE_FILTERS = {
  spof_potentiel: { label: "SPOF potentiel", cls: "text-orange-400", dot: "#FF9F0A" },
  alt_non_verifiee: { label: "Alternative non vérifiée", cls: "text-orange-300", dot: "#FFB020" },
  redondance_partielle: { label: "Redondance partielle", cls: "text-yellow-400", dot: "#FFD60A" },
  redondance_verifiee: { label: "Redondance vérifiée", cls: "text-green-400", dot: "#32D74B" },
  resilience_degradee: { label: "Résilience dégradée", cls: "text-yellow-400", dot: "#FF9F0A" },
};
export const RESILIENCE_FILTER_KEYS = Object.keys(RESILIENCE_FILTERS);
export const tResilienceFilter = (v) => RESILIENCE_FILTERS[v]?.label || v;
// VULNERABILITY (Lot 1) — vocabulaire CONTRÔLÉ (Literal backend) -> libellés FR.
// La gravité (severity) est INTRINSÈQUE : ce n'est jamais un niveau de RISQUE CROS.
export const VULN_TYPES = {
  SOFTWARE_VULNERABILITY: "Vulnérabilité logicielle",
  CONFIGURATION_WEAKNESS: "Faiblesse de configuration",
  ARCHITECTURE_WEAKNESS: "Faiblesse d'architecture",
  IDENTITY_WEAKNESS: "Faiblesse d'identité",
  UNSUPPORTED_SOFTWARE: "Logiciel non supporté",
  WEAK_SEGMENTATION: "Segmentation faible",
  EXCESSIVE_PRIVILEGES: "Privilèges excessifs",
  OTHER: "Autre",
};
export const VULN_TYPE_KEYS = Object.keys(VULN_TYPES);

export const SEVERITY = {
  CRITICAL: { label: "Critique", cls: "bg-red-500/15 text-red-400 border-red-500/30", dot: "#FF3B30" },
  HIGH: { label: "Élevée", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30", dot: "#FF9F0A" },
  MEDIUM: { label: "Moyenne", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", dot: "#FFD60A" },
  LOW: { label: "Faible", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30", dot: "#0A84FF" },
  INFO: { label: "Informative", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", dot: "#71717A" },
  UNKNOWN: { label: "Inconnue", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", dot: "#52525B" },
};
export const SEVERITY_KEYS = Object.keys(SEVERITY);

// STATUT = traitement administratif. ACCEPTED != acceptation de RISQUE (moteur Risk indépendant).
export const VULN_STATUS = {
  OPEN: { label: "Ouverte", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  ACKNOWLEDGED: { label: "Reconnue", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  IN_REMEDIATION: { label: "En remédiation", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  MITIGATED: { label: "Atténuée", cls: "bg-teal-500/15 text-teal-400 border-teal-500/30" },
  ACCEPTED: { label: "Acceptée (traitement)", cls: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  CLOSED: { label: "Fermée", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  FALSE_POSITIVE: { label: "Faux positif", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const VULN_STATUS_KEYS = Object.keys(VULN_STATUS);

export const tVulnType = (v) => VULN_TYPES[v] || v;
export const tSeverity = (v) => SEVERITY[v]?.label || v;
export const tVulnStatus = (v) => VULN_STATUS[v]?.label || v;

export const tRelStatus = (v) => REL_STATUS[v]?.label || v;
export const tDirection = (v) => DIRECTIONS[v] || v;
export const tAltStatus = (v) => ALT_STATUS[v] || v;
export const tSwitch = (v) => SWITCH_MODE[v] || v;
export const tValidation = (v) => VALIDATION[v] || v;

// THREAT (Lot 2A) — vocabulaire CROS CONTRÔLÉ. Les catégories ne remplacent JAMAIS
// les techniques MITRE ATT&CK (référentiel dédié, Lot 2B).
export const THREAT_CATEGORIES = {
  RANSOMWARE: "Rançongiciel",
  DDOS: "Déni de service (DDoS)",
  INSIDER: "Menace interne",
  SUPPLY_CHAIN: "Chaîne d'approvisionnement",
  PHISHING: "Hameçonnage",
  MALWARE: "Logiciel malveillant",
  APT: "Menace persistante avancée (APT)",
  DATA_BREACH: "Violation de données",
  PHYSICAL_SABOTAGE: "Sabotage physique",
  OTHER: "Autre",
};
export const THREAT_CATEGORY_KEYS = Object.keys(THREAT_CATEGORIES);

// Statut métier de la menace. archived_at est indépendant (archiver ne force pas RETIRED).
export const THREAT_STATUS = {
  ACTIVE: { label: "Active", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  MONITORED: { label: "Surveillée", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  DORMANT: { label: "Dormante", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  RETIRED: { label: "Retirée", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  FALSE_POSITIVE: { label: "Faux positif", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const THREAT_STATUS_KEYS = Object.keys(THREAT_STATUS);

export const ACTOR_TYPES = {
  NATION_STATE: "État-nation", CYBERCRIMINAL: "Cybercriminel", HACKTIVIST: "Hacktiviste",
  INSIDER: "Interne", OPPORTUNISTIC: "Opportuniste", UNKNOWN: "Inconnu",
};
export const ACTOR_TYPE_KEYS = Object.keys(ACTOR_TYPES);

// Confiance d'une association THREAT↔VULNERABILITY — saisie analyste, jamais calculée.
export const LINK_CONFIDENCE = { LOW: "Faible", MEDIUM: "Moyenne", HIGH: "Élevée" };
export const LINK_CONFIDENCE_KEYS = Object.keys(LINK_CONFIDENCE);

export const tThreatCategory = (v) => THREAT_CATEGORIES[v] || v;
export const tThreatStatus = (v) => THREAT_STATUS[v]?.label || v;
export const tActorType = (v) => (v ? ACTOR_TYPES[v] || v : null);
export const tConfidence = (v) => LINK_CONFIDENCE[v] || v;

// MITRE ATT&CK (Lot 2B) — référentiel officiel v19.2. Le référentiel est GLOBAL et
// en lecture seule ; seul le mapping THREAT —USES→ TECHNIQUE est écrit par l'analyste.
export const MITRE_MATRICES = { enterprise: "Enterprise", ics: "ICS", mobile: "Mobile" };
export const MITRE_MATRIX_KEYS = Object.keys(MITRE_MATRICES);
export const MITRE_TACTICS = {
  reconnaissance: "Reconnaissance",
  "resource-development": "Développement de ressources",
  "initial-access": "Accès initial",
  execution: "Exécution",
  persistence: "Persistance",
  "privilege-escalation": "Élévation de privilèges",
  "defense-evasion": "Évasion défensive",
  "credential-access": "Accès aux identifiants",
  discovery: "Découverte",
  "lateral-movement": "Déplacement latéral",
  collection: "Collecte",
  "command-and-control": "Commande et contrôle",
  exfiltration: "Exfiltration",
  impact: "Impact",
  stealth: "Furtivité",
  evasion: "Évasion",
  "inhibit-response-function": "Inhibition des fonctions de réponse",
  "impair-process-control": "Altération du contrôle du processus",
};
export const tMatrix = (v) => MITRE_MATRICES[v] || v;
export const tTactic = (v) => MITRE_TACTICS[v] || v;

// CONTROL (Lot 3) — vocabulaire CONTRÔLÉ. status = cycle de vie ADMINISTRATIF (jamais
// l'implémentation ni l'efficacité). L'efficacité est attestée séparément (control:attest).
export const CONTROL_FUNCTIONS = {
  preventive: "Préventif", detective: "Détectif", corrective: "Correctif",
  recovery: "Récupération", compensating: "Compensatoire",
};
export const CONTROL_FUNCTION_KEYS = Object.keys(CONTROL_FUNCTIONS);
export const CONTROL_STATUS = {
  DRAFT: { label: "Brouillon", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  ACTIVE: { label: "Actif", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  SUSPENDED: { label: "Suspendu", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  RETIRED: { label: "Retiré", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
};
export const CONTROL_STATUS_KEYS = Object.keys(CONTROL_STATUS);
// Efficacité ATTESTÉE — jamais un niveau de risque. `creditable`/`is_expired` sont dérivés.
export const EFFECTIVENESS = {
  high: { label: "Élevée", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  medium: { label: "Moyenne", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  low: { label: "Faible", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  unknown: { label: "Non attestée", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const EFFECTIVENESS_KEYS = Object.keys(EFFECTIVENESS);
export const ATTEST_RATING_KEYS = ["high", "medium", "low"];
export const CONTROL_FRAMEWORKS = [
  "NIST_CSF", "ISO_27001", "ISO_27002", "CIS", "NIS2", "DORA", "RECYF", "PCI_DSS",
];
export const PROTECT_TARGET_TYPES = {
  asset: "Actif", service: "Service", relationship: "Relation", failure_domain: "Domaine de défaillance",
};
export const PROTECT_TARGET_KEYS = Object.keys(PROTECT_TARGET_TYPES);
export const tControlFunction = (v) => CONTROL_FUNCTIONS[v] || v;
export const tControlStatus = (v) => CONTROL_STATUS[v]?.label || v;
export const tEffectiveness = (v) => EFFECTIVENESS[v]?.label || v;
export const tProtectTarget = (v) => PROTECT_TARGET_TYPES[v] || v;

// RISK INHÉRENT (Lot 4) — vocabulaire ORDINAL déterministe (moteur core/risk.py).
// Aucun score numérique global : le niveau est LOW|MODERATE|HIGH|CRITICAL.
export const INHERENT_LEVEL = {
  LOW: { label: "Faible", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  MODERATE: { label: "Modéré", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  HIGH: { label: "Élevé", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  CRITICAL: { label: "Critique", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};
export const INHERENT_LEVEL_KEYS = Object.keys(INHERENT_LEVEL);
export const LIKELIHOOD = {
  RARE: "Rare", UNLIKELY: "Improbable", POSSIBLE: "Possible", LIKELY: "Probable", ALMOST_CERTAIN: "Quasi certain",
};
export const IMPACT = {
  NEGLIGIBLE: "Négligeable", MINOR: "Mineur", MODERATE: "Modéré", MAJOR: "Majeur", CRITICAL: "Critique",
};
export const RISK_CONFIDENCE = { LOW: "Faible", MEDIUM: "Moyenne", HIGH: "Élevée" };
export const RISK_TREATMENT = {
  OPEN: "Ouvert", MITIGATING: "En atténuation", TRANSFERRED: "Transféré",
  AVOIDED: "Évité", ACCEPTED_PENDING: "Acceptation en attente",
};
export const RISK_TREATMENT_KEYS = Object.keys(RISK_TREATMENT);
export const tInherentLevel = (v) => INHERENT_LEVEL[v]?.label || v;
export const tLikelihood = (v) => LIKELIHOOD[v] || v;
export const tImpact = (v) => IMPACT[v] || v;
export const tRiskConfidence = (v) => RISK_CONFIDENCE[v] || v;
export const tTreatment = (v) => RISK_TREATMENT[v] || v;

// SCENARIO (Lot 5) — vocabulaire déclaratif partagé (contrat, réutilisé par le futur moteur).
export const SCENARIO_ORIGIN = {
  manual: "Manuel", predefined: "Prédéfini", generated: "Généré", simulated: "Simulé", historical: "Historique",
};
export const SCENARIO_STATUS = {
  draft: { label: "Brouillon", cls: "bg-muted text-muted-foreground border-border" },
  validated: { label: "Validé", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  archived: { label: "Archivé", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const SCENARIO_STATUS_KEYS = Object.keys(SCENARIO_STATUS);
export const SCENARIO_EVENT_TYPE = {
  asset_compromised: "Actif compromis", service_disrupted: "Service perturbé", credential_theft: "Vol d'identifiants",
  lateral_movement: "Mouvement latéral", data_exfiltration: "Exfiltration de données", encryption: "Chiffrement",
  denial_of_service: "Déni de service", failover_triggered: "Bascule déclenchée", containment: "Confinement",
  recovery_started: "Rétablissement démarré", recovery_completed: "Rétablissement terminé",
};
export const SCENARIO_EVENT_KEYS = Object.keys(SCENARIO_EVENT_TYPE);
export const SIMULATED_STATE = {
  OPERATIONAL: "Opérationnel", DEGRADED: "Dégradé", UNAVAILABLE: "Indisponible", COMPROMISED: "Compromis",
  ISOLATED: "Isolé", RECOVERING: "En rétablissement", RECOVERED: "Rétabli",
};
export const SIMULATED_STATE_KEYS = Object.keys(SIMULATED_STATE);
export const tScenarioOrigin = (v) => SCENARIO_ORIGIN[v] || v;
export const tScenarioStatus = (v) => SCENARIO_STATUS[v]?.label || v;
export const tEventType = (v) => SCENARIO_EVENT_TYPE[v] || v;
export const tSimState = (v) => SIMULATED_STATE[v] || v;

// SIMULATION (Lot 7) — l'état simulé what-if est isolé au run (jamais l'état réel).
// Styles/couleurs de l'overlay cartographique (baseline vs simulé) et du causal trace.
export const SIM_STATE_STYLE = {
  OPERATIONAL: { label: "Opérationnel", color: "#32D74B", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  DEGRADED: { label: "Dégradé", color: "#FFD60A", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  UNAVAILABLE: { label: "Indisponible", color: "#FF3B30", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  COMPROMISED: { label: "Compromis", color: "#FF375F", cls: "bg-rose-500/15 text-rose-400 border-rose-500/30" },
  ISOLATED: { label: "Isolé", color: "#0A84FF", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  RECOVERING: { label: "En rétablissement", color: "#FF9F0A", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  RECOVERED: { label: "Rétabli", color: "#64D2FF", cls: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
};
export const simStateColor = (v) => SIM_STATE_STYLE[v]?.color || "#71717A";
export const simStateCls = (v) => SIM_STATE_STYLE[v]?.cls || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
// origin = distinction EXPLICITE injecté (hypothèse analyste) vs dérivé (propagation moteur).
export const TRANSITION_ORIGIN = {
  injected: { label: "Injecté", cls: "bg-primary/15 text-primary border-primary/30" },
  derived: { label: "Dérivé", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
};
export const tOrigin = (v) => TRANSITION_ORIGIN[v]?.label || v;

// RISQUE RÉSIDUEL (Lot 8) — dérivé (inhérent + preuves de simulation + contrôles applicables).
// Réutilise le vocabulaire ordinal INHERENT_LEVEL (LOW|MODERATE|HIGH|CRITICAL). Aucun score global.
export const tResidualLevel = (v) => INHERENT_LEVEL[v]?.label || v;
export const residualLevelCls = (v) => INHERENT_LEVEL[v]?.cls || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";

// CYBER RESILIENCE SCORE (CRS v1) — score 0-100 PAR SERVICE (100 = meilleure résilience). Aucun score global.
export const CRS_BAND = {
  "FORTE RÉSILIENCE": { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", ring: "#32D74B" },
  "ROBUSTE": { cls: "bg-green-500/15 text-green-400 border-green-500/30", ring: "#30D158" },
  "MODÉRÉ": { cls: "bg-blue-500/15 text-blue-400 border-blue-500/30", ring: "#0A84FF" },
  "FAIBLE": { cls: "bg-orange-500/15 text-orange-400 border-orange-500/30", ring: "#FF9F0A" },
  "CRITIQUE": { cls: "bg-red-500/15 text-red-400 border-red-500/30", ring: "#FF3B30" },
  "NON CALCULABLE": { cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", ring: "#71717A" },
};
export const crsBandCls = (v) => CRS_BAND[v]?.cls || CRS_BAND["NON CALCULABLE"].cls;
export const crsRing = (v) => CRS_BAND[v]?.ring || "#71717A";
export const CRS_DIM_LABELS = {
  "D-STR": "Structure", "D-VEX": "Exposition", "D-CTL": "Contrôles", "D-SIM": "Simulation", "D-REC": "Rétablissement",
};
export const crsBarColor = (v) => {
  if (v == null) return "#3f3f46";
  if (v >= 75) return "#30D158";
  if (v >= 60) return "#0A84FF";
  if (v >= 40) return "#FF9F0A";
  return "#FF3B30";
};

// CYBER DIGITAL TWIN (Lot 9) — l'état observé BRUT réutilise SIMULATED_STATE (7 états figés).
// La FRAÎCHEUR et la DIVERGENCE sont DÉRIVÉES par le moteur (jamais persistées). Aucun score composite.
export const TWIN_FRESHNESS = {
  FRESH: { label: "À jour", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  AGING: { label: "Vieillissante", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
  STALE: { label: "Périmée", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  UNKNOWN: { label: "Inconnue", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
};
export const tFreshness = (v) => TWIN_FRESHNESS[v]?.label || v;
export const twinFreshnessCls = (v) => TWIN_FRESHNESS[v]?.cls || TWIN_FRESHNESS.UNKNOWN.cls;

export const TWIN_DIVERGENCE = {
  "TWIN-DIV-OBS-WORSE-SIM": "Réel pire que simulé",
  "TWIN-DIV-OBS-WORSE-STR": "Réel dégradé vs structure",
  "TWIN-DIV-SIM-WORSE-OBS": "Modèle pessimiste vs réel",
  "TWIN-DIV-ALIGNED": "Aligné",
  "TWIN-COV-UNKNOWN": "Non observé",
};
export const tDivergence = (v) => TWIN_DIVERGENCE[v] || v;

export const TWIN_SEVERITY = {
  HIGH: { label: "Élevée", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  MEDIUM: { label: "Moyenne", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  LOW: { label: "Faible", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  UNKNOWN: { label: "Inconnue", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  NONE: { label: "—", cls: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20" },
};
export const tTwinSeverity = (v) => TWIN_SEVERITY[v]?.label || v;
export const twinSeverityCls = (v) => TWIN_SEVERITY[v]?.cls || TWIN_SEVERITY.UNKNOWN.cls;
export const TWIN_CONFIDENCE = { HIGH: "Élevée", MEDIUM: "Moyenne", LOW: "Faible" };
export const TWIN_CONFIDENCE_KEYS = Object.keys(TWIN_CONFIDENCE);
export const covPct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

export default api;
